import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSelector } from '@/components/ui/language-selector';
import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const Login = () => {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [error, setError] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const { t } = useTranslation('common');
	const { login, isAuthenticated, isLoading } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (isAuthenticated) {
			navigate('/', { replace: true });
		}
	}, [isAuthenticated, navigate]);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError('');

		try {
			setSubmitting(true);
			await login({ username, password });
			if (rememberMe) {
				window.localStorage.setItem(
					'vectro-station:last-username',
					username
				);
			}
			navigate('/', { replace: true });
		} catch (authError) {
			// Differentiate between network errors and authentication errors
			const isNetworkError =
				authError instanceof Error &&
				(authError.message.includes('Failed to fetch') ||
					authError.message.includes('NetworkError') ||
					authError.message.includes('Network request failed') ||
					authError.message.includes('ERR_CONNECTION') ||
					authError.message.includes('ECONNREFUSED') ||
					authError.name === 'TypeError');

			if (isNetworkError) {
				setError(t('login.server_unreachable'));
			} else {
				// Authentication error (invalid credentials)
				setError(t('login.invalid_credentials'));
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div
			className={cn(
				'flex',
				'min-h-screen',
				'items-center',
				'justify-center',
				'p-4',
				'bg-background',
				'relative',
				'overflow-hidden'
			)}
		>
			{/* Language Selector */}
			<div className="absolute top-4 right-4 z-20">
				<LanguageSelector />
			</div>

			{/* Background Pattern */}
			<div
				className={cn(
					'absolute',
					'inset-0',
					'opacity-[0.03]',
					'dark:opacity-[0.05]'
				)}
				style={{
					backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%239C92AC' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
					backgroundSize: '40px 40px'
				}}
			/>

			<div
				className={cn(
					'w-full',
					'max-w-md',
					'space-y-8',
					'relative',
					'z-10'
				)}
			>
				{/* Header */}
				<div className="text-center">
					<div
						className={cn(
							'mx-auto',
							'flex',
							'h-14',
							'w-14',
							'items-center',
							'justify-center',
							'rounded-full',
							'bg-primary/20',
							'dark:bg-primary/30',
							'mb-6'
						)}
					>
						<svg
							className="h-7 w-7 text-primary"
							fill="none"
							viewBox="0 0 48 48"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								clipRule="evenodd"
								d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z"
								fill="currentColor"
								fillRule="evenodd"
							/>
						</svg>
					</div>
					<h1
						className={cn(
							'text-3xl',
							'font-bold',
							'tracking-tight',
							'text-foreground'
						)}
					>
						{t('login.title')}
					</h1>
					<p
						className={cn(
							'mt-2',
							'text-sm',
							'text-muted-foreground'
						)}
					>
						{t('login.subtitle')}
					</p>
				</div>

				{/* Login Form Card */}
				<div
					className={cn(
						'bg-card',
						'shadow-lg',
						'rounded-lg',
						'border',
						'border-border',
						'p-8'
					)}
				>
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Error Message */}
						{error && (
							<div
								className={cn(
									'flex',
									'items-center',
									'gap-2',
									'p-3',
									'text-sm',
									'text-destructive',
									'bg-destructive/10',
									'border',
									'border-destructive/20',
									'rounded-lg'
								)}
							>
								<AlertCircle className="h-4 w-4 shrink-0" />
								<span>{error}</span>
							</div>
						)}

						{/* Username Field */}
						<div>
							<Label
								htmlFor="username"
								className={cn(
									'block',
									'text-sm',
									'font-medium',
									'text-foreground',
									'mb-2'
								)}
							>
								{t('login.username')}
							</Label>
							<Input
								id="username"
								name="username"
								type="text"
								autoComplete="username"
								required
								placeholder={t('login.username_placeholder')}
								value={username}
								onChange={e => setUsername(e.target.value)}
								className={cn(
									'w-full',
									'px-3',
									'py-2',
									'bg-background',
									'border',
									'border-input',
									'rounded-lg',
									'focus:ring-2',
									'focus:ring-primary',
									'focus:border-primary'
								)}
							/>
						</div>

						{/* Password Field */}
						<div>
							<Label
								htmlFor="password"
								className={cn(
									'block',
									'text-sm',
									'font-medium',
									'text-foreground',
									'mb-2'
								)}
							>
								{t('login.password')}
							</Label>
							<Input
								id="password"
								name="password"
								type="password"
								autoComplete="current-password"
								required
								placeholder={t('login.password_placeholder')}
								value={password}
								onChange={e => setPassword(e.target.value)}
								className={cn(
									'w-full',
									'px-3',
									'py-2',
									'bg-background',
									'border',
									'border-input',
									'rounded-lg',
									'focus:ring-2',
									'focus:ring-primary',
									'focus:border-primary'
								)}
							/>
						</div>

						{/* Remember Me & Forgot Password */}
						<div
							className={cn(
								'flex',
								'items-center',
								'justify-between',
								'flex-wrap',
								'gap-2'
							)}
						>
							<div className="flex items-center space-x-2">
								<Checkbox
									id="remember-me"
									checked={rememberMe}
									onCheckedChange={checked =>
										setRememberMe(checked === true)
									}
								/>
								<Label
									htmlFor="remember-me"
									className={cn(
										'text-sm',
										'text-foreground',
										'cursor-pointer'
									)}
								>
									{t('login.remember_me')}
								</Label>
							</div>
							{/* <a
								href="#"
								className={cn(
									'text-sm',
									'font-medium',
									'text-primary',
									'hover:text-primary/80',
									'transition-colors'
								)}
							>
								{t('login.forgot_password')}
							</a> */}
						</div>

						{/* Submit Button */}
						<Button
							type="submit"
							disabled={isLoading || submitting}
							className={cn(
								'w-full',
								'py-2',
								'px-3',
								'text-sm',
								'font-semibold',
								'bg-primary',
								'text-primary-foreground',
								'rounded-lg',
								'hover:bg-primary/90',
								'focus:ring-2',
								'focus:ring-primary',
								'focus:ring-offset-2',
								'disabled:opacity-50',
								'disabled:cursor-not-allowed'
							)}
						>
							{submitting
								? 'Signing in...'
								: t('login.login_button')}
						</Button>
					</form>
				</div>
			</div>
		</div>
	);
};
